import type { NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useState } from 'react';

import { DepositsLayout, Meta } from '@/layouts';
import { MapDepositsFromServer } from '@/services/supabase';
import { Main } from '@/templates/Main';
import type { IBankDepositCard } from '@/types';
import { calculateHighestRate } from '@/utils/calculateHighestRate';
import { makeServerProps } from '@/utils/nextjs';
import { useSeoText } from '@/utils/useSeoText';

import ApiSingleton from '../../../services/ApiSingleton';

export const getServerSideProps = makeServerProps()
  .withAction(async ({ locale, query }, _, userCountry) => {
    const apiSingleton = ApiSingleton.getInstance();
    try {
      const country = userCountry.name;
      const preparedTerms: any = 'all';
      let isTest;
      if (query?.test_user === 'true') {
        isTest = true;
      }
      if (query?.test_user === 'false') {
        isTest = false;
      }

      const { data: featuredDeposits, meta: initialPartnerDepositsMeta } =
        await apiSingleton.getDepositsListRequest(locale || 'en', {
          is_test: isTest,
          page: 1,
          countries: [userCountry.alpha2],
          currencies:
            userCountry.currency === 'all' ? undefined : [userCountry.currency],
          product_types: ['fixed term deposit'],
          deposit_types: ['ordinary deposit'],
          is_partner: true,
        });

      const featuredDepositCards: IBankDepositCard[] = featuredDeposits?.map(
        (deposit: any) => MapDepositsFromServer(deposit),
      )!;

      const { data: deposits, meta: initialDepositsMeta } =
        await apiSingleton.getDepositsListRequest(locale || 'en', {
          is_test: isTest,
          page: 1,
          countries: [userCountry.alpha2],
          currencies:
            userCountry.currency === 'all' ? undefined : [userCountry.currency],
          product_types: ['fixed term deposit'],
          deposit_types: ['ordinary deposit'],
          is_partner: false,
        });

      const depositCards: IBankDepositCard[] =
        deposits.length !== 0
          ? deposits?.map((deposit: any) => MapDepositsFromServer(deposit))
          : [];

      const translations = await serverSideTranslations(locale ?? 'en', [
        'common',
      ]);

      return {
        props: {
          ...translations,
          initialDeposits: depositCards,
          initialPartnerDeposits: featuredDepositCards,
          initialDepositsMeta,
          initialPartnerDepositsMeta,
          preparedTerms,
          country,
        },
      };
    } catch (error) {
      console.log(error);
      return {
        notFound: true,
      };
    }
  })
  .build();

export const SearchPage: NextPage<any> = ({
  initialDeposits,
  initialPartnerDeposits,
  initialDepositsMeta,
  initialPartnerDepositsMeta,
  country,
}) => {
  const { t } = useTranslation();
  const [highestRate, setHighestRate] = useState(
    calculateHighestRate(
      initialDeposits[0]?.grossInterestRate,
      initialPartnerDeposits[0]?.grossInterestRate,
    ),
  );

  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const monthYearString = `${month}/${year}`;

  const generateH1 = () => {
    return t('fixed-deposits.h1', {
      country: t(`database.countries.${country}`),
      date: monthYearString,
      percentage: highestRate,
    });
  };
  const { getFixedDepositsSections } = useSeoText();

  return (
    <Main
      shouldHeaderBeBlue
      meta={
        <Meta
          title={t('seo.fixed-deposits.main.title', {
            date: monthYearString,
          })}
          description={t('seo.fixed-deposits.main.description')}
          keywords={t('pages.deposits.keywords')}
        />
      }
    >
      <DepositsLayout
        initialDeposits={initialDeposits}
        initialDepositsMeta={initialDepositsMeta}
        initialPartnerDeposits={initialPartnerDeposits}
        initialPartnerDepositsMeta={initialPartnerDepositsMeta}
        setHighestRate={setHighestRate}
        h1={generateH1()}
        sections={getFixedDepositsSections({
          country,
          monthYear: monthYearString,
          maxRate: highestRate || 0,
        })}
      />
    </Main>
  );
};

export default SearchPage;
